// OfflineBanner.tsx — R2 Offline Mode Indicator
// Shows a non-intrusive banner when the user is operating offline.
// Reads the offline token from localStorage and displays days remaining.

import { useState, useEffect } from 'react';

const API = (import.meta as any).env?.VITE_BACKEND_URL ?? '';
const LS_OFFLINE_TOKEN = 'r2_offline_token';

interface OfflineTokenPayload {
  uid: string; oid: string; role: string; plan: string;
  modules: string[]; offline_days: number; exp: number; tid: string;
}

function parseJwtPayload(token: string): OfflineTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const raw = atob((parts[1] ?? '').replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(raw) as OfflineTokenPayload;
  } catch {
    return null;
  }
}

type NetStatus = 'online' | 'offline' | 'checking';

export function OfflineBanner() {
  const [netStatus, setNetStatus]     = useState<NetStatus>('checking');
  const [tokenData, setTokenData]     = useState<OfflineTokenPayload | null>(null);
  const [daysLeft, setDaysLeft]       = useState<number>(0);
  const [dismissed, setDismissed]     = useState(false);
  const [syncing, setSyncing]         = useState(false);

  // Probe the API every 30s
  const probe = async () => {
    try {
      const r = await fetch(`${API}/api/health`, { method: 'GET', signal: AbortSignal.timeout(4000) });
      setNetStatus(r.ok ? 'online' : 'offline');
    } catch {
      setNetStatus('offline');
    }
  };

  useEffect(() => {
    probe();
    const iv = setInterval(probe, 30_000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem(LS_OFFLINE_TOKEN);
    if (!raw) return;
    const payload = parseJwtPayload(raw);
    if (payload) {
      setTokenData(payload);
      const secLeft = payload.exp - Math.floor(Date.now() / 1000);
      setDaysLeft(Math.max(0, Math.floor(secLeft / 86400)));
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('r2_auth_token');
      const offlineToken = localStorage.getItem(LS_OFFLINE_TOKEN);
      const payload = offlineToken ? parseJwtPayload(offlineToken) : null;
      await fetch(`${API}/api/license/offline/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ device_id: payload?.tid || '', usage_data: {} }),
      });
      setNetStatus('online');
      setDismissed(true);
    } catch {
      // ignore
    } finally {
      setSyncing(false);
    }
  };

  if (dismissed || netStatus === 'checking') return null;
  if (netStatus === 'online') return null; // Hide when online

  // Offline mode banner
  const isExpired = daysLeft <= 0;
  const urgentColor = isExpired ? '#f87171' : daysLeft <= 3 ? '#f59e0b' : '#5eead4';

  return (
    <div data-testid="offline-banner" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
      background: isExpired ? 'rgba(248,113,113,0.12)' : 'rgba(94,234,212,0.08)',
      backdropFilter: 'blur(8px)',
      borderBottom: `1px solid ${urgentColor}30`,
      padding: '8px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: urgentColor, boxShadow: `0 0 6px ${urgentColor}` }} />
        <span style={{ fontSize: 10, color: urgentColor, fontWeight: 700, letterSpacing: '0.1em' }}>
          R2 OFFLINE MODE
        </span>
        <span style={{ fontSize: 10, color: 'rgba(100,116,139,0.8)' }}>
          {isExpired
            ? '— Offline authorization expired. Reconnect immediately.'
            : `— Operating with local authorization. Reconnect within ${daysLeft} day${daysLeft !== 1 ? 's' : ''} to synchronize.`}
        </span>
        {tokenData && !isExpired && (
          <span style={{ fontSize: 9, color: 'rgba(100,116,139,0.5)', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 10 }}>
            Modules: {tokenData.modules.slice(0, 2).join(', ')}{tokenData.modules.length > 2 ? ` +${tokenData.modules.length - 2}` : ''}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button data-testid="offline-sync-btn" onClick={handleSync} disabled={syncing}
          style={{ padding: '4px 12px', background: `${urgentColor}15`, border: `1px solid ${urgentColor}40`, borderRadius: 5, color: urgentColor, fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', fontFamily: 'inherit' }}>
          {syncing ? 'SYNCING…' : 'RECONNECT'}
        </button>
        <button onClick={() => setDismissed(true)}
          style={{ padding: '4px 8px', background: 'none', border: 'none', color: 'rgba(100,116,139,0.5)', cursor: 'pointer', fontSize: 11 }}>
          ✕
        </button>
      </div>
    </div>
  );
}
